/**
 * The five "Automated Operations" totals, sourced from the CRM Events `/overview`
 * endpoint and scoped to the ACTIVE brand's tenant (same per-brand scoping the rest
 * of the dashboard uses). Replaces the section's `useMockEndpoint` with the real
 * fetch, keeping the exact `{ stats, loading }` shape `AutomatedStats` consumes.
 *
 * The tile chrome (icon + label) is preserved from `baseStats`; only each tile's
 * `value` is overridden by its matching real total. On a failed request the figures
 * read `N/A` (the dashboard's API-failing marker); while a brand resolves or the
 * request is in flight it reports `loading` so the tiles show their skeletons.
 *
 * NOTE the operations pipeline returns all-zero totals for every tenant in test
 * until the backend provisions data, so these read live but show `0` for now (the
 * wiring is correct and lights up when real totals land). Contract:
 * `docs/api.owners-hub.operations.md`.
 */
import React from 'react';

import { useAppSelector } from '@/store';

import type { AutomatedStat } from '../../mock-data/types';
import { getOperationsOverview, type OperationsOverview } from './api/crm-events';

/** Each "Automated Operations" tile id mapped to its `/overview` total field. */
const FIELD_BY_ID: Record<string, keyof OperationsOverview> = {
  campaigns: 'campaigns_total',
  targeted: 'players_targeted_total',
  notifications: 'sent_notifications_total',
  activated: 'players_activated_total',
  support: 'support_cases_total',
};

const formatTotal = (value: number): string => value.toLocaleString('en-US');

export type OperationsOverviewResult = { stats: AutomatedStat[]; loading: boolean };

/**
 * @param baseStats The static tile set (icon + label per id) from mock-data; its
 *        `value`s are overridden with the real totals once they load.
 */
export const useOperationsOverview = (baseStats: AutomatedStat[]): OperationsOverviewResult => {
  const tenantId = useAppSelector((state) => state.casinoInstance.displayBrand?.tenant_id);
  const [overview, setOverview] = React.useState<OperationsOverview | null>(null);
  const [status, setStatus] = React.useState<'loading' | 'loaded' | 'error'>('loading');

  React.useEffect(() => {
    // No brand resolved yet: keep the section loading until one does.
    if (!tenantId) {
      setStatus('loading');
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    setOverview(null);
    getOperationsOverview({ tenantId }, controller.signal)
      .then((data) => {
        setOverview(data);
        setStatus('loaded');
      })
      .catch(() => {
        // Ignore the abort that the cleanup triggers; surface a real failure as `N/A`.
        if (!controller.signal.aborted) setStatus('error');
      });
    return () => controller.abort();
  }, [tenantId]);

  const stats = React.useMemo<AutomatedStat[]>(() => {
    if (status === 'loaded' && overview) {
      return baseStats.map((stat) => {
        const field = FIELD_BY_ID[stat.id];
        return field ? { ...stat, value: formatTotal(overview[field]) } : stat;
      });
    }
    if (status === 'error') {
      return baseStats.map((stat) => (FIELD_BY_ID[stat.id] ? { ...stat, value: 'N/A' } : stat));
    }
    // Loading: the tiles show skeletons, so the values are not read.
    return baseStats;
  }, [status, overview, baseStats]);

  return { stats, loading: status === 'loading' };
};

export default useOperationsOverview;
